import { useEffect, useState } from 'react';
import { LogOut, X } from 'lucide-react';
import baseapi, { API_BASE } from '../../baseapi';
import { LiveFrame } from '../junctions/LiveFrame';

/** The stages of the main exit route and the camera that watches each one (null = no camera there yet). */
export interface ExitStage { label: string; name: string; te: string; cam: string | null }

export const EXIT1_STAGES: ExitStage[] = [
  { label: 'S-10', name: 'Ravi Chettu open place', te: 'రావి చెట్టు', cam: 'CAM-0-88' },                 // 192.168.0.88
  { label: 'S-14', name: 'Temple Through Steps Exit', te: 'మెట్ల మార్గం నిష్క్రమణ', cam: 'CAM-0-58' },    // 192.168.0.58
  { label: 'S-16', name: 'Annadanam', te: 'అన్నదానం', cam: null },
  { label: 'S-17', name: 'Prasadam & laddu counters', te: 'ప్రసాదం కౌంటర్లు', cam: null },
  { label: 'S-15', name: 'Kanaka Durga Nagar exit', te: 'కనకదుర్గ నగర్ ఎగ్జిట్', cam: 'CAM-0-213' },     // 192.168.0.213
];

/** Second way out: from the Antharalayam (two exit lines, VIP and normal) past Mouna Muni. */
export const EXIT2_STAGES: ExitStage[] = [
  { label: 'S-9', name: 'Antharalayam VIP Exit', te: 'అంతరాలయం వి.ఐ.పి నిష్క్రమణ', cam: 'CAM-2-44' },        // 192.168.2.44
  { label: 'S-9', name: 'Antharalayam Normal Exit', te: 'అంతరాలయం సాధారణ నిష్క్రమణ', cam: 'CAM-2-125' },   // 192.168.2.125 (free line)
  { label: 'S-11', name: 'Mouna Muni', te: 'మౌన ముని', cam: 'CAM-2-123' },                                   // 192.168.2.123
];

/** One row of GET /api/cameras/overview. */
interface Cam {
  id: string; name: string; ip: string | null; state: string; online: boolean; analytics?: boolean;
  tracks: number | null;
  today: { people_toward: number; people_away: number };
}

const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-IN'));
/** Cell colour by how many people the camera sees: the same green -> amber -> red steps as the queue zones. */
const fillColor = (n: number) => (n >= 80 ? '#E5533D' : n >= 50 ? '#F0A63A' : n >= 30 ? '#E8C56B' : n >= 15 ? '#8BCB85' : '#25CFA2');

/**
 * Main exit route (Antharalayam -> Kanaka Durga Nagar), from the cameras only.
 * Each stage shows the people its camera sees right now; under it, the people who crossed that camera's
 * counting line today (where a line is drawn). The camera button opens the live picture.
 */
export function ExitRouteStrip({ onStalls, title = 'Exit 1 · Main Exit Route (Antharalayam ➔ Kanaka Durga Nagar)', stages = EXIT1_STAGES }: {
  /** opens the prasadam stalls plan */ onStalls?: () => void; title?: string; stages?: ExitStage[];
}) {
  const STAGES = stages;
  const [cams, setCams] = useState<Record<string, Cam> | null>(null);
  const [view, setView] = useState<{ cam: Cam; stage: string } | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () => baseapi.get('cameras/overview')
      .then(r => { if (alive && Array.isArray(r.data?.cameras)) setCams(Object.fromEntries(r.data.cameras.map((c: Cam) => [c.id, c]))); })
      .catch(() => { /* keep the last figures */ });
    load();
    const t = window.setInterval(load, 5000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);

  const cols = { gridTemplateColumns: `repeat(${STAGES.length}, minmax(0, 1fr))` };

  return (
    <div className="bg-white dark:bg-[#181b22] border border-neutral-200/80 dark:border-[#262b37] rounded-xl p-3 shadow-xs">
      <div className="text-sm tracking-wide text-neutral-900 dark:text-neutral-100 font-bold mb-2.5 flex flex-wrap items-center justify-between gap-2 border-b border-neutral-100 dark:border-[#222733] pb-2">
        <div className="flex items-center gap-2">
          <LogOut className="w-4 h-4 text-teal-500" />
          <span>{title}</span>
        </div>
        <span className="text-[11px] text-neutral-400 font-normal">People each stage's camera sees now · crossed its line today</span>
      </div>

      <div className="overflow-x-auto w-full pb-1">
        <div className="min-w-[760px] bg-neutral-50/80 dark:bg-[#14171f] border border-neutral-200/60 dark:border-[#222733] rounded-lg overflow-hidden">
          <div className="grid divide-x divide-neutral-200/60 dark:divide-[#222733] border-b border-neutral-200/60 dark:border-[#222733] bg-neutral-100/70 dark:bg-[#1c202a]" style={cols}>
            {STAGES.map(st => (
              <div key={st.name} className="px-2.5 py-1.5 min-w-0" title={`${st.name} — ${st.te}`}>
                <div className="flex items-center justify-between gap-1">
                  <span className="font-mono text-[10.5px] text-blue-600 dark:text-blue-400 font-bold tracking-wider">{st.label}</span>
                  {st.label === 'S-17' && onStalls && (
                    <button type="button" onClick={onStalls} title="Open the prasadam stalls plan"
                      className="px-1.5 py-0.5 rounded border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[9px] font-bold tracking-wide cursor-pointer hover:bg-amber-100">STALLS PLAN</button>
                  )}
                </div>
                <span className="block text-[10.5px] text-neutral-800 dark:text-neutral-200 font-semibold leading-tight truncate">{st.name}</span>
                <em className="text-[8.5px] text-neutral-400 not-italic truncate leading-none mt-0.5 block">{st.te}</em>
              </div>
            ))}
          </div>

          <div className="grid gap-1 px-1 py-1" style={cols}>
            {STAGES.map(st => {
              const cam = st.cam && cams ? cams[st.cam] : undefined;
              if (!st.cam || (cams && !cam)) {
                return (
                  <div key={st.name} title={`${st.name}: no camera yet`}
                    className="h-[36px] rounded border border-dashed border-neutral-200 dark:border-[#2a3040] bg-neutral-100/30 dark:bg-[#1f2430]/20 flex items-center justify-center text-neutral-400 text-xs">
                    —
                  </div>
                );
              }
              const counting = !!cam && cam.online && cam.analytics !== false && cam.tracks != null;
              const viewOnly = !!cam && cam.analytics === false;
              const offline = !!cam && !cam.online && cam.state !== 'standby';
              const n = counting ? cam!.tracks! : 0;
              return (
                <div key={st.name} className="h-[36px] rounded flex items-center justify-between px-2.5 shadow-xs transition hover:brightness-105"
                  style={{ background: counting ? fillColor(n) : offline ? '#9CA3AF' : '#94A3B8' }}
                  title={cam ? `${st.name} · ${cam.name}${cam.ip ? ` · ${cam.ip}` : ''} — ${counting ? `${n} people in view now` : offline ? 'camera offline' : viewOnly ? 'view only: this camera does not count' : 'no figure yet'}` : st.name}>
                  <span className="font-mono text-sm font-bold truncate" style={{ color: '#07231C' }}>
                    {!cam ? '…' : counting ? fmt(n) : offline ? 'offline' : viewOnly ? 'view only' : '—'}
                  </span>
                  {cam && (
                    <button type="button" onClick={() => setView({ cam, stage: `${st.label} · ${st.name}` })}
                      title={`Open the live camera at ${st.name}`} aria-label={`Camera at ${st.name}`}
                      className="p-1 rounded bg-black/40 hover:bg-black/70 text-white transition cursor-pointer flex items-center justify-center shrink-0 ml-1 active:scale-95">
                      <svg viewBox="0 0 24 16" width="13" height="9" aria-hidden="true">
                        <path d="M1 3h13v10H1z" fill="currentColor" />
                        <path d="M15 6l7-4v12l-7-4z" fill="currentColor" />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="grid divide-x divide-neutral-200/60 dark:divide-[#222733] border-t border-neutral-200/60 dark:border-[#222733] bg-neutral-100/70 dark:bg-[#1c202a]" style={cols}>
            {STAGES.map(st => {
              const cam = st.cam && cams ? cams[st.cam] : undefined;
              const crossed = cam && cam.analytics !== false ? Math.max(cam.today?.people_toward ?? 0, cam.today?.people_away ?? 0) : null;
              return (
                <div key={st.name} className="px-2.5 py-1 font-mono text-[10px] text-neutral-500 dark:text-neutral-400 flex items-center justify-between gap-1 min-w-0 tabular-nums">
                  <span className="truncate">{cam ? (cam.ip ?? cam.id) : 'no camera'}</span>
                  <span className="truncate">{crossed ? <>crossed today <b className="text-neutral-800 dark:text-neutral-200">{fmt(crossed)}</b></> : ''}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {view && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setView(null)}>
          <div className="w-[1100px] max-w-full bg-[#0f1218] border border-[#262b37] rounded-xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-3 py-2 border-b border-[#262b37] text-neutral-100">
              <span className="text-[13px] font-bold truncate">{view.stage}</span>
              <span className="text-[11px] text-neutral-400 truncate">{view.cam.name} · {view.cam.id}{view.cam.ip ? ` · ${view.cam.ip}` : ''}</span>
              <button onClick={() => setView(null)} className="ml-auto p-1 rounded hover:bg-white/10 cursor-pointer" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            <div className="aspect-video bg-black">
              <LiveFrame src={`${API_BASE}cameras/${encodeURIComponent(view.cam.id)}/snapshot.jpg`} alt={view.cam.id} className="w-full h-full object-contain" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
